// Die Zuordnung einer Aktivitaet (Anruf, Notiz, Termin), einer Aufgabe oder einer Mail
// zu Kundenanfragen — dieselbe Liste an allen drei Stellen (Aktivitaeten nur beim
// BEARBEITEN, siehe unten; Aufgaben und Mails auch beim Anlegen).
//
// Aktivitaeten kennen genau EINE Anfrage (Radio, `Activities.objectID`), Aufgaben und
// Mails beliebig viele (Checkbox, `isMulti`) — deshalb hier ueber die Prop entschieden
// statt drei fast gleicher Komponenten.
//
// Zwei Betriebsarten fuer die Daten: mit `companyId` allein holt die Komponente die
// Anfragen selbst (Aktivitaeten-Bearbeiten kennt nur die Firma). Wer `requests`
// mitgibt (Aufgaben- und Mail-Fenster laden sie ohnehin zusammen mit den Kontakten in
// einem Rutsch), bekommt keinen zweiten Request — die Komponente rendert dann nur noch.
// In dieser Betriebsart entscheidet der Aufrufer selbst, ob und wann er die Komponente
// ueberhaupt zeigt (siehe TaskFormModal/EmailAssignmentModal).
//
// Beim Anlegen einer Aktivitaet wird bewusst nicht gefragt: dort entsteht die
// Zuordnung aus dem Verlauf, aus dem heraus der Vorgang festgehalten wird, und ein
// Auswahlfeld waere eine Frage mitten im Erfassen (Stoppuhr laeuft, Ergebnis fehlt
// noch). Wer von der Firmenseite aus erfasst hat, traegt die Anfrage danach im
// Bearbeiten-Fenster nach.
//
// ponytail: Firma und Kontaktperson bleiben bei Aktivitaeten bewusst aussen vor. Ein
// Firmenwechsel muss `companyOwnerUserId` und `leadShare` mitziehen, und darauf haben
// die Vertriebsrollen kein `update` — das braucht den Lambda-Weg wie bei der Mail
// (B-VII).
//
// Auftraege: mit `orders` (kontrolliert) oder `withOrders` (selbst ladend) wird die
// Ueberschrift zu zwei Tabs, darunter wechseln Suche und Liste. Mehrfachauswahl haelt
// beide Typen getrennt (`values`/`onChange`, `orderValues`/`onOrderChange`); in der
// Einzelauswahl gibt es EINE Verknuepfung ueber beide Tabs, `onChange(id, type)`.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Form, Spinner, InputGroup, Nav } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import RequestItemImages from "./RequestItemImages";
import { client } from "../helpers/amplifyClient";
import { customerRequestsByCompanyID } from "./graphql/taskForm.queries";
import { ordersByCompanyID } from "./graphql/activityFeed.queries";
import { dateFormatter } from "./DateFormatter";

// Gesucht wird ueber Nummer, ID und das ANGEZEIGTE Datum, nicht den ISO-Wert — der
// Nutzer tippt, was er sieht (dateFormatter).
export const filterRequests = (requests, search) => {
  const needle = search.trim().toLowerCase();
  if (!needle) return requests;
  return requests.filter((request) => {
    const date = request.createdAt ? dateFormatter(request.createdAt).toLowerCase() : "";
    return (
      (request.number || "").toLowerCase().includes(needle) ||
      (request.id || "").toLowerCase().includes(needle) ||
      date.includes(needle)
    );
  });
};

// Auftraege in die Form der Anfrage-Zeilen bringen: Suche und Liste bleiben eine.
const orderRows = (orders) =>
  (orders || []).map((order) => ({
    id: order.id,
    number: order.orderNumber,
    createdAt: order.createdAt,
  }));

const RequestSelect = ({
  companyId,
  requests: requestsProp,
  loading: loadingProp = false,
  isMulti = false,
  value,
  values,
  onChange,
  disabled,
  orders: ordersProp,
  ordersLoading = false,
  withOrders = false,
  orderValues,
  onOrderChange,
  valueType,
}) => {
  const { t } = useTranslation(["components", "common"]);
  // Kontrolliert: der Aufrufer liefert die Liste, kein eigener Request.
  const controlled = requestsProp !== undefined;
  const showOrders = withOrders || ordersProp !== undefined;
  const [fetched, setFetched] = useState([]);
  const [fetchedOrders, setFetchedOrders] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(valueType === "Order" ? "orders" : "requests");
  const listRef = useRef(null);
  const savedScrollTop = useRef(null);

  // Ein Klick sortiert die Anfrage nach oben (siehe `visible`). React gibt der
  // verschobenen Checkbox danach den Fokus zurueck, und der Browser scrollt sie dabei
  // ins Bild — die Liste spraenge also mit nach oben. Die Position vor dem Klick wird
  // hier nach dem Rendern, aber vor dem Zeichnen wiederhergestellt.
  useLayoutEffect(() => {
    if (savedScrollTop.current === null || !listRef.current) return;
    listRef.current.scrollTop = savedScrollTop.current;
    savedScrollTop.current = null;
  });

  useEffect(() => {
    if (controlled) return;
    let cancelled = false;
    if (!companyId) {
      setFetched([]);
      return;
    }
    setFetching(true);
    (async () => {
      try {
        const [response, orderResponse] = await Promise.all([
          client.graphql({
            query: customerRequestsByCompanyID,
            variables: { companyID: companyId },
          }),
          // Eigener catch: wer keine Auftraege lesen darf, behaelt die Anfragen.
          withOrders
            ? client
                .graphql({
                  query: ordersByCompanyID,
                  variables: { companyId, limit: 100 },
                })
                .catch((error) => {
                  console.log("Auftraege nicht lesbar", error);
                  return null;
                })
            : null,
        ]);
        if (cancelled) return;
        setFetched(
          response?.data?.customerRequestsByComanyIdByCreatedAt?.items || []
        );
        setFetchedOrders(
          orderResponse?.data?.ordersByCompanyIdByOrderNumber?.items || []
        );
      } catch (error) {
        console.log("Kundenanfragen nicht lesbar", error);
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, controlled, withOrders]);

  // Im kontrollierten Modus entscheidet der Aufrufer per JSX, ob er die Komponente
  // ueberhaupt zeigt (Task) oder immer (Mail) — hier nur die selbst ladende Variante
  // ausblenden, solange keine Firma feststeht.
  if (!controlled && !companyId) return null;

  const onOrders = showOrders && tab === "orders";
  const requests = onOrders
    ? orderRows(ordersProp !== undefined ? ordersProp : fetchedOrders)
    : controlled
      ? requestsProp
      : fetched;
  const loading =
    (controlled ? loadingProp : fetching) ||
    (onOrders && ordersProp !== undefined && ordersLoading);
  const requestIds = isMulti ? values || [] : value ? [value] : [];
  const orderIds = isMulti ? orderValues || [] : value ? [value] : [];
  const selectedIds = onOrders ? orderIds : requestIds;
  // Einzelauswahl: in welchem Tab die eine Verknuepfung liegt (fuer die Zahl am Tab).
  // Ein anderer Typ (Produktanfrage, Angebot) liegt in keinem — ohne Typ ist es eine
  // aeltere Kundenanfrage.
  const singleTab = !value
    ? null
    : valueType === "Order"
      ? "orders"
      : !valueType || valueType === "CustomerRequest"
        ? "requests"
        : null;
  // Ausgewaehlte Anfragen stehen oben, damit man sie in langen Listen nicht suchen
  // muss; sonst bleibt die Reihenfolge der Abfrage (sort ist stabil).
  const visible = [...filterRequests(requests, search)].sort(
    (a, b) => selectedIds.includes(b.id) - selectedIds.includes(a.id)
  );

  const emit = (next) => {
    savedScrollTop.current = listRef.current?.scrollTop ?? null;
    if (!isMulti) onChange(next, next ? (onOrders ? "Order" : "CustomerRequest") : "");
    else if (onOrders) onOrderChange(next);
    else onChange(next);
  };

  const toggle = (id) => {
    if (!isMulti) {
      emit(value === id ? "" : id);
      return;
    }
    emit(
      selectedIds.includes(id)
        ? selectedIds.filter((existing) => existing !== id)
        : [...selectedIds, id]
    );
  };

  // Bezieht sich nur auf das, was die Suche gerade zeigt — sonst waehlt "Alle" auch
  // Zeilen aus, die der Nutzer weggefiltert hat und nie gesehen hat.
  const selectAll = () => {
    const ids = (search.trim() ? visible : requests).map((r) => r.id);
    emit([...new Set([...selectedIds, ...ids])]);
  };

  const deselectAll = () => {
    const ids = new Set((search.trim() ? visible : requests).map((r) => r.id));
    emit(selectedIds.filter((id) => !ids.has(id)));
  };

  const bulkButtons = isMulti && requests.length > 1 && (
    <div className="small">
      <Button
        variant="link"
        size="sm"
        className="p-0 me-2 text-decoration-none"
        onClick={selectAll}
      >
        {t("Select all")}
      </Button>
      <span className="text-muted">|</span>
      <Button
        variant="link"
        size="sm"
        className="p-0 ms-2 text-decoration-none"
        onClick={deselectAll}
      >
        {t("Deselect all")}
      </Button>
    </div>
  );

  return (
    <Form.Group className="mb-3">
      {/* Mit Auftraegen: die Tabs in eigener Zeile, "Alle auswaehlen" darunter. */}
      {showOrders ? (
        <>
          <Nav
            as="ul"
            variant="tabs"
            activeKey={tab}
            onSelect={(key) => {
              setTab(key);
              setSearch("");
            }}
            className="nav-bordered mb-2"
          >
            {[
              ["requests", isMulti ? "Customer Requests" : "Customer Request", requestIds],
              ["orders", isMulti ? "Orders" : "Order", orderIds],
            ].map(([key, label, ids]) => {
              // Was im ANDEREN Tab haengt, sieht man sonst nicht.
              const count = isMulti ? ids.length : singleTab === key ? 1 : 0;
              return (
                <Nav.Item as="li" key={key}>
                  <Nav.Link eventKey={key} className="py-1">
                    {t(label)}
                    {count > 0 && <span className="text-muted small ms-1">{count}</span>}
                  </Nav.Link>
                </Nav.Item>
              );
            })}
          </Nav>
          {bulkButtons && (
            <div className="d-flex justify-content-end mb-1">{bulkButtons}</div>
          )}
        </>
      ) : (
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Form.Label className="mb-0">
            {t(isMulti ? "Customer Requests" : "Customer Request")}
          </Form.Label>
          {bulkButtons}
        </div>
      )}

      {loading ? (
        <div className="py-2 text-muted d-flex align-items-center gap-2">
          <Spinner size="sm" />
          <span className="small">{t("Loading...")}</span>
        </div>
      ) : requests.length === 0 ? (
        <p className="text-muted mb-0 small">
          {t(onOrders ? "This company has no orders." : "This company has no requests.")}
        </p>
      ) : (
        <>
          {requests.length > 5 && (
            <InputGroup size="sm" className="mb-2">
              <Form.Control
                size="sm"
                placeholder={t(onOrders ? "Search orders..." : "Search customer requests...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <InputGroup.Text
                  onClick={() => setSearch("")}
                  style={{ cursor: "pointer", backgroundColor: "#fff" }}
                >
                  <i className="mdi mdi-close text-muted" />
                </InputGroup.Text>
              )}
            </InputGroup>
          )}
          <div
            ref={listRef}
            className="border rounded p-2 bg-light bg-opacity-25"
            style={{ maxHeight: 180, overflowY: "auto" }}
          >
            {/* Die Zuordnung wieder loesen koennen ist kein Zusatz: ohne diese Zeile
                bliebe eine einmal falsch gesetzte Anfrage fuer immer dran. Bei
                Mehrfachauswahl uebernimmt das schlicht "kein Haken". */}
            {!isMulti && (
              <Form.Check
                type="radio"
                id="request-select-none"
                className="mb-1"
                label={<span className="text-muted">{t("No assignment")}</span>}
                checked={!value}
                disabled={disabled}
                onChange={() => emit("")}
              />
            )}
            {visible.length === 0 ? (
              <p className="text-muted mb-0 small p-1">
                {t(onOrders ? "No orders found." : "No customer requests found.")}
              </p>
            ) : (
              visible.map((request) => (
                <div
                  key={request.id}
                  className="d-flex align-items-center gap-2 mb-1"
                >
                  <Form.Check
                    type={isMulti ? "checkbox" : "radio"}
                    id={`request-select-${request.id}`}
                    className="mb-0"
                    label={
                      <span className="d-flex align-items-center gap-2">
                        <span className="fw-semibold">
                          {request.number || request.id}
                        </span>
                        {request.createdAt && (
                          <span className="text-muted small">
                            ({dateFormatter(request.createdAt)})
                          </span>
                        )}
                      </span>
                    }
                    checked={selectedIds.includes(request.id)}
                    disabled={disabled}
                    onChange={() => toggle(request.id)}
                  />
                  {!onOrders && <RequestItemImages request={request} />}
                  <a
                    href={onOrders ? `/order/${request.id}` : `/request/edit/${request.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted text-decoration-none d-inline-flex align-items-center"
                    title={t("Open in new tab")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="mdi mdi-open-in-new font-14" />
                  </a>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </Form.Group>
  );
};

export default RequestSelect;
